/**
 * Which Axis API a live link is read from, and on which routes.
 *
 * v2 (axis.outerlink.com) and v3 (axis.outerlink.app) have separate databases, so a link is only known to the
 * console that created it. Links created in v2 keep their original shape, livelink.outerlink.app/{id}, and are read
 * from the v2 API. Links created in v3 are livelink.outerlink.app/v3/{id} and are read from the v3 API, which serves
 * the viewer from its public /api/v3/livelink routes, keyed by the live link.
 */
export type LiveLinkApiVersion = 'v2' | 'v3';

export interface LiveLinkApiConfig {
  VITE_API_BASE_URL?: string;
  VITE_API_BASE_URL_V3?: string;
}

export interface LiveLinkApi {
  /** Undefined when the deployment does not configure this API, so the viewer can say so. */
  baseUrl?: string;
  liveLinkUrl: (liveLinkId: string) => string;
  reportUrl: (liveLinkId: string, assetId: string) => string;
  hubUrl: string;
}

/**
 * @param runtimeConfig written by livelink-entrypoint.sh at container start; an unset variable arrives as ''
 * @param buildConfig Vite's build-time env, used when the runtime value is missing or empty
 */
export const resolveLiveLinkApi = (
  version: LiveLinkApiVersion,
  runtimeConfig: LiveLinkApiConfig | undefined,
  buildConfig: LiveLinkApiConfig
): LiveLinkApi => {
  if (version === 'v3') {
    const baseUrl = runtimeConfig?.VITE_API_BASE_URL_V3 || buildConfig.VITE_API_BASE_URL_V3 || undefined;

    return {
      baseUrl,
      liveLinkUrl: (liveLinkId) => `${baseUrl}/api/v3/livelink/${liveLinkId}`,
      reportUrl: (liveLinkId) => `${baseUrl}/api/v3/livelink/${liveLinkId}/report`,
      hubUrl: `${baseUrl}/livelinkhub`
    };
  }

  const baseUrl = runtimeConfig?.VITE_API_BASE_URL || buildConfig.VITE_API_BASE_URL || undefined;

  return {
    baseUrl,
    liveLinkUrl: (liveLinkId) => `${baseUrl}/api/livelinks/${liveLinkId}`,
    reportUrl: (liveLinkId, assetId) => `${baseUrl}/api/assets/${assetId}/report?liveLinkId=${liveLinkId}`,
    hubUrl: `${baseUrl}/hubs/livelink`
  };
};
